import { useCallback, useEffect, useRef, useState } from 'react'
import Auth from 'services/authService'
import endpoints from '../constants/endpoints'
import { useToast } from '@chakra-ui/core'
import { useFirstUserMessageInChatGPT } from './useFirstUserMessageInChatGPT'
import { useVoicePlayer } from './useVoicePlayer'
import { IPromptInjection } from 'constants/interfaces'

const MAX_CHUNK_SIZE = 120 * 1024

export interface ConversationMessage {
  message: string
  isUserMessage?: boolean
  isFakeMessage?: boolean
}

export interface SendMessageInterface {
  is_init?: boolean
  text?: string
  data?: any
  group_id?: string
  prompt_id?: string
  images?: string[]
}

export const useChatGPT = ({
  enabled,
  isInjectReports,
  isInstantConversation,
  onError,
  onReInitialize,
  prompt_id,
  group_id,
  mode = 1, // 0 - text, 1 - voice
  play = true, // Controls voice playback
  customInitData, // Custom data for init message (for AI cases)
  customInitImages, // Custom images for init message (for AI cases)
  promptInjections,
}: {
  enabled: boolean
  isInjectReports?: boolean
  isInstantConversation?: boolean
  onError?: () => void
  onReInitialize?: () => void
  prompt_id?: string
  group_id?: string
  mode?: number
  play?: boolean
  customInitData?: string
  customInitImages?: string[]
  promptInjections?: IPromptInjection[]
}) => {
  const toast = useToast()

  const [isWebSocketReady, setIsWebsocketReady] = useState(false)
  const [isBotResponseInProgress, setIsBotResponseInProgress] = useState(false)
  const [conversation, setConversation] = useState<ConversationMessage[]>([])
  const [pendingReportData, setPendingReportData] = useState<string | null>(null)
  const [isSessionExpired, setIsSessionExpired] = useState(false)

  // Use refs for custom init message to allow dynamic updates
  const customInitDataRef = useRef<string | undefined>(customInitData)
  const customInitImagesRef = useRef<string[] | undefined>(customInitImages)

  // Update refs when props change
  useEffect(() => {
    customInitDataRef.current = customInitData
    customInitImagesRef.current = customInitImages
  }, [customInitData, customInitImages])

  const ws = useRef<WebSocket | null>(null)
  const sessionId = useRef<string | null>(null)
  const lastInteractionDate = useRef<number | null>(null)
  const textChunks = useRef<string[]>([])
  const chunkIndexToSend = useRef(0)
  const initMessageData = useRef<SendMessageInterface | null>(null)
  const numberOfReconnects = useRef(0)
  const initMessageResetTrigger = useRef(0)
  const reportDataSent = useRef(false) // Track if report data was already sent
  const pendingReportDataRef = useRef<string | null>(null) // Keep ref in sync with state
  const resetInitMessageFlag = useCallback(() => {
    // Increment trigger to make useFirstUserMessageInChatGPT react
    initMessageResetTrigger.current += 1
    reportDataSent.current = false // Reset flag for reconnection
    console.log('Resetting init message flag for reconnection')
  }, [])

  // Voice player hook for mode 1 (voice mode)
  const { addVoiceChunk, resetPlayback } = useVoicePlayer(mode === 1, play)

  // Check if session is expired (for both instant and non-instant conversation modes)
  const checkSessionExpiry = useCallback(() => {
    if (!lastInteractionDate.current) return false

    const now = new Date().getTime()
    const timePassedInSeconds = (now - lastInteractionDate.current) / 1000
    const TEN_MINUTES = 10 * 60 // Using 20 seconds for testing

    return timePassedInSeconds >= TEN_MINUTES
  }, [])
  // Sync ref with state
  useEffect(() => {
    pendingReportDataRef.current = pendingReportData
  }, [pendingReportData])

  useFirstUserMessageInChatGPT({
    isInjectReports: enabled && (isInjectReports || false) && !isSessionExpired,
    isInstantConversation: isInstantConversation || false,
    webSocket: ws.current,
    isWebSocketReady,
    setIsBotResponseInProgress,
    prompt_id,
    initMessageResetTrigger: initMessageResetTrigger.current,
    setPendingReportData,
    lastInteractionDate: lastInteractionDate.current,
    mode,
    group_id: group_id || null,
    customInitData: customInitDataRef.current, // Pass custom init data from ref
    customInitImages: customInitImagesRef.current, // Pass custom init images from ref
    promptInjections,
  })

  const splitMessageIntoChunks = useCallback(
    ({ text, data, group_id, prompt_id, images }: SendMessageInterface) => {
      let remainingText = text

      while (remainingText && remainingText.length > 0) {
        let low = 0
        let high = remainingText.length

        while (low < high) {
          const mid = Math.ceil((low + high) / 2)
          const candidateChunk = remainingText.slice(0, mid)

          const messageToMeasure = {
            action: 'ws_conversation',
            data,
            mode,
            message: { data: candidateChunk },
            group_id,
            ...(prompt_id ? { prompt_id } : {}),
            ...(images ? { images } : {}),
            ...(sessionId.current ? { session_id: sessionId.current } : {}),
          }

          const serializedSize = new TextEncoder().encode(JSON.stringify(messageToMeasure)).length

          if (serializedSize <= MAX_CHUNK_SIZE) {
            low = mid
          } else {
            high = mid - 1
          }
        }

        const chunk = remainingText.slice(0, low)
        textChunks.current.push(chunk)
        remainingText = remainingText.slice(low)
      }

      chunkIndexToSend.current = 0
    },
    [mode]
  )

  const handleSendTextChunks = useCallback(
    ({ is_init, data, group_id: msgGroupId, prompt_id: msgPromptId, images }: SendMessageInterface) => {
      if (textChunks.current.length === 0) return

      for (let chunkIndex = chunkIndexToSend.current; chunkIndex < textChunks.current.length; chunkIndex++) {
        if (is_init && !initMessageData.current) {
          initMessageData.current = { is_init, data, group_id: msgGroupId, prompt_id: msgPromptId, images }
        }

        if (!sessionId.current && chunkIndex !== 0) break

        const chunk = textChunks.current[chunkIndex]
        const messageWithChunk = {
          action: 'ws_conversation',
          is_init,
          data,
          mode,
          message: {
            data: chunk,
            total_chunks: textChunks.current.length,
            chunk_index: chunkIndex + 1,
          },
          group_id: msgGroupId || group_id,
          ...(msgPromptId || prompt_id ? { prompt_id: msgPromptId || prompt_id } : {}),
          ...(images ? { images } : {}),
          ...(sessionId.current ? { session_id: sessionId.current } : {}),
        }

        ws.current?.send(JSON.stringify(messageWithChunk))

        if (chunkIndex === textChunks.current.length - 1) {
          textChunks.current = []
          chunkIndexToSend.current = 0
          initMessageData.current = null
        } else {
          chunkIndexToSend.current = chunkIndex + 1
        }
      }
    },
    [mode, group_id, prompt_id]
  )

  const sendMessage = useCallback(
    async ({ is_init, text, data, group_id: msgGroupId, prompt_id: msgPromptId, images }: SendMessageInterface) => {
      // Stop voice playback immediately when user sends a new message (requirement #4)
      if (mode === 1) {
        resetPlayback()
      }

      // Check if session is expired before sending
      if (checkSessionExpiry()) {
        console.log('Session expired, blocking message send')
        setIsSessionExpired(true)
        return
      }

      // If the socket is not open, initialize it
      console.log(group_id)

      if (ws.current?.readyState !== WebSocket.OPEN) {
        console.log('WebSocket not ready, initializing...')
        resetInitMessageFlag() // Reset the flag before reconnecting
        await initialize()
        // Wait for the socket to open and call the send function directly
        return new Promise<void>((resolve) => {
          const checkReady = () => {
            if (ws.current?.readyState === WebSocket.OPEN) {
              // Call the send function directly without recursion
              doSendMessage({ is_init, text, data, group_id: msgGroupId, prompt_id: msgPromptId, images })
              resolve()
            } else {
              setTimeout(checkReady, 100)
            }
          }
          checkReady()
        })
      }

      // Send message immediately - report data will be attached if available
      doSendMessage({ is_init, text, data, group_id: msgGroupId, prompt_id: msgPromptId, images })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      handleSendTextChunks,
      splitMessageIntoChunks,
      prompt_id,
      group_id,
      resetInitMessageFlag,
      isInstantConversation,
      checkSessionExpiry,
      mode,
      resetPlayback,
    ]
  )

  const doSendMessage = useCallback(
    ({ is_init, text, data, group_id: msgGroupId, prompt_id: msgPromptId, images }: SendMessageInterface) => {
      if (ws.current?.readyState === WebSocket.OPEN) {
        setIsBotResponseInProgress(true)
        lastInteractionDate.current = new Date().getTime()

        if (is_init && !sessionId.current && !initMessageData.current) {
          initMessageData.current = { is_init, text, data, group_id: msgGroupId, prompt_id: msgPromptId, images }
        }

        // For instant conversation mode, attach report data to first user message (not is_init)
        let messageText = text
        if (!is_init && text && isInstantConversation && !reportDataSent.current) {
          // Mark that we processed first user message (with or without report data)
          reportDataSent.current = true

          if (pendingReportDataRef.current) {
            // Only attach report data to the first user message (not is_init)
            messageText = `${pendingReportDataRef.current}\n\n${text}`
            setPendingReportData(null) // Clear after using for first message
            pendingReportDataRef.current = null // Clear ref too
          }
        }

        if (!messageText || new TextEncoder().encode(JSON.stringify(messageText)).length <= MAX_CHUNK_SIZE) {
          const messageToSend = {
            action: 'ws_conversation',
            is_init,
            mode,
            ...(messageText ? { message: { data: messageText } } : {}),
            data,
            group_id: msgGroupId || group_id,
            ...(msgPromptId || prompt_id ? { prompt_id: msgPromptId || prompt_id } : {}),
            ...(images ? { images } : {}),
            ...(sessionId.current ? { session_id: sessionId.current } : {}),
          }

          ws.current.send(JSON.stringify(messageToSend))
          textChunks.current = []
          chunkIndexToSend.current = 0
        } else {
          splitMessageIntoChunks({
            text: messageText,
            data,
            group_id: msgGroupId || group_id,
            ...(msgPromptId || prompt_id ? { prompt_id: msgPromptId || prompt_id } : {}),
            ...(images ? { images } : {}),
          })

          handleSendTextChunks({
            is_init,
            data,
            group_id: msgGroupId || group_id,
            ...(msgPromptId || prompt_id ? { prompt_id: msgPromptId || prompt_id } : {}),
            ...(images ? { images } : {}),
          })
        }
      }
    },
    [group_id, isInstantConversation, mode, prompt_id, splitMessageIntoChunks, handleSendTextChunks]
  )

  const initialize = useCallback(async () => {
    // Check if session is expired before initializing
    if (checkSessionExpiry()) {
      console.log('Session expired during initialization')
      setIsSessionExpired(true)
      return
    }

    const savedSessionId = localStorage.getItem('chatgpt_session_id')
    if (savedSessionId) {
      sessionId.current = savedSessionId
    }

    const refreshedIdToken = await Auth.getUpdatedToken()
    const websocket = new WebSocket(`${endpoints.socketGPT(refreshedIdToken)}`)

    websocket.onopen = () => {
      if (websocket.readyState === WebSocket.OPEN) {
        setIsWebsocketReady(true)
      }
    }

    websocket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)

        lastInteractionDate.current = new Date().getTime()

        if (data.session_id) {
          sessionId.current = data.session_id
          localStorage.setItem('chatgpt_session_id', data.session_id)
        }

        // Handle voice chunks BEFORE checking for completion (mode 1)
        if (mode === 1 && data.res_msg?.voice) {
          addVoiceChunk(data.res_msg.voice)
        }

        // Handle completion
        if (data.res_complete) {
          if (initMessageData.current && textChunks.current.length) {
            handleSendTextChunks(initMessageData.current)
            return
          }
          setIsBotResponseInProgress(false)
          return
        }

        if (data.res_msg?.text && typeof data.res_msg.text === 'string') {
          let rawChunk: string | undefined
          try {
            const parsed = JSON.parse(data.res_msg.text)
            if (parsed && typeof parsed.text === 'string' && parsed.text.length > 0) {
              rawChunk = parsed.text
            }
          } catch {
            rawChunk = undefined
          }
          if (rawChunk) {
            const textChunk = rawChunk
            setConversation((prev) => {
              if (prev.length > 0 && !prev[prev.length - 1].isUserMessage) {
                return prev.map((msg, idx) =>
                  idx === prev.length - 1 ? { ...msg, message: msg.message + textChunk } : msg
                )
              }
              return [...prev, { message: textChunk }]
            })
          }
        }
      } catch (e) {
        setIsBotResponseInProgress(false)
      }
    }

    websocket.onerror = () => {
      setIsWebsocketReady(false)
      toast({
        title: 'WebSocket error',
        description: 'Please try again later',
        status: 'error',
      })
      ws.current?.close()
    }

    ws.current = websocket
  }, [handleSendTextChunks, toast, checkSessionExpiry, mode, addVoiceChunk])

  const disconnectWebSocket = useCallback(() => {
    if (ws.current) {
      setIsWebsocketReady(false)
      setIsBotResponseInProgress(false)
      ws.current?.close()
    }
  }, [])

  const resetSessionId = useCallback(() => {
    sessionId.current = null
    lastInteractionDate.current = null
    reportDataSent.current = false // Reset flag when session is reset
    localStorage.removeItem('chatgpt_session_id')
  }, [])

  // Reload session - clear everything and start fresh
  const reloadSession = useCallback(() => {
    console.log('Reloading session - starting fresh...')
    setIsSessionExpired(false)
    resetSessionId()
    setConversation([])
    setPendingReportData(null)
    reportDataSent.current = false
    resetInitMessageFlag()

    // Reinitialize WebSocket to start fresh flow
    if (enabled) {
      initialize()
    }
  }, [enabled, initialize, resetSessionId, resetInitMessageFlag])

  const clearConversationHistory = useCallback(() => {
    resetSessionId()
    setConversation([])
  }, [resetSessionId])

  const clearSessionOnPopClose = useCallback(() => {
    sessionId.current = null
    lastInteractionDate.current = null
    reportDataSent.current = false // Reset flag when session is closed
    localStorage.removeItem('chatgpt_session_id')
  }, [])

  useEffect(() => {
    if (enabled) {
      initialize()
    } else if (ws.current) {
      disconnectWebSocket()
    }
  }, [enabled, initialize, disconnectWebSocket])

  useEffect(() => {
    if (ws.current) {
      ws.current.onerror = async () => {
        setIsWebsocketReady(false)
        setIsBotResponseInProgress(false)
        onError?.()
        ws.current?.close()

        if (numberOfReconnects.current <= 3) {
          numberOfReconnects.current += 1
          await initialize()
        }
      }
    }
  }, [initialize, onError])

  useEffect(() => {
    if (ws.current?.readyState === WebSocket.OPEN || ws.current?.readyState === WebSocket.CONNECTING) {
      ws.current.close()
    }

    return () => {
      ws.current?.close()
    }
  }, [initialize])

  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined

    if (isWebSocketReady) {
      intervalId = setInterval(() => {
        if (lastInteractionDate.current) {
          const now = new Date().getTime()
          const timePassedInSeconds = (now - lastInteractionDate.current) / 1000

          if (timePassedInSeconds >= 10 * 60) {
            console.log('WebSocket closed due to timeout')
            setIsSessionExpired(true) // Set session as expired
            resetSessionId()
            setIsWebsocketReady(false)
            setIsBotResponseInProgress(false)
            ws.current?.close()
            // Do not call initialize() - we will open the socket only when sending a message
            onReInitialize?.()
          }
        }
      }, 1000)
    }

    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [initialize, isWebSocketReady, onReInitialize, resetSessionId])

  return {
    conversation,
    setConversation,
    sendMessage,
    isBotResponseInProgress,
    setIsBotResponseInProgress,
    isWebSocketReady,
    disconnectWebSocket,
    resetSessionId,
    clearConversationHistory,
    clearSessionOnPopClose,
    isSessionExpired,
    reloadSession,
    stopVoicePlayback: resetPlayback, // Expose voice playback control
  }
}
